// gallera-voice FASE 2 — WebRTC no navegador do atendente.
// Portado do WaCalls (client/src/lib/webrtc.ts + pcm.ts): áudio do mic vira
// Int16 PCM 16kHz enviado por DataChannel; o retorno é tocado por AudioWorklet.
// A troca de SDP passa pelo NOSSO backend (proxy autenticado /voice/...), que
// repassa ao motor. O motor nunca é exposto ao browser.
import api from "../services/api";

const SAMPLE_RATE = 16000;
const PCM_CHANNEL_LABEL = "pcm";
const CAPTURE_WORKLET_URL = "/worklets/capture-processor.js";
const PLAYBACK_WORKLET_URL = "/worklets/playback-processor.js";
const CAPTURE_PROCESSOR_NAME = "capture-processor";
const PLAYBACK_PROCESSOR_NAME = "playback-processor";

// Prefixo único de log p/ depurar produção (filtrar no console por "[GALLERA-VOICE]").
const LOG = "[GALLERA-VOICE]";

// ── DESTRAVA DE ÁUDIO (autoplay policy) ──────────────────────────────────────
// Navegadores (Chrome/Safari) BLOQUEIAM AudioContext/áudio sem um GESTO do usuário
// (autoplay policy). Enquanto o AudioContext fica "suspended", o AudioWorklet de
// playback NÃO processa o PCM do celular → o stream que vai pro <audio> fica MUDO
// (mesmo o <audio>.play() "funcionando").
//
// ASSIMETRIA que causava "ligar não traz áudio, atender traz":
//   • ATENDER: openGalleraCall (onde o ctx.resume acontece) roda na cadeia do clique
//     "Atender" (handleAnswer é onClick) → gesto presente → resume libera.
//   • LIGAR:   o clique "Ligar" só ORIGINA a chamada (POST + window event); o card
//     monta depois e o openGalleraCall roda num useEffect DESACOPLADO do clique →
//     sem gesto na pilha → o AudioContext da chamada podia ficar "suspended".
//
// Fix idiomático: DESTRAVAR o áudio SOB O GESTO (todo clique de ligar/atender, e no
// 1º gesto da página) resumindo um AudioContext PERSISTENTE + tocando um buffer mudo.
// Uma vez destravado sob gesto, o documento fica "audio-allowed" e os AudioContexts
// das chamadas (mesmo os criados no useEffect do outbound) entram em "running".
// Idempotente e barato: um único contexto reaproveitado por sessão.
let unlockCtx = null;
export const ensureAudioUnlocked = () => {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    if (!unlockCtx) unlockCtx = new AudioCtx();
    if (unlockCtx.state === "suspended") {
      unlockCtx.resume().then(
        () => {
          // eslint-disable-next-line no-console
          console.log(`${LOG} ensureAudioUnlocked: áudio DESTRAVADO sob gesto (state=${unlockCtx.state})`);
        },
        (e) => {
          // eslint-disable-next-line no-console
          console.warn(`${LOG} ensureAudioUnlocked: resume falhou (segue)`, e?.message || e);
        }
      );
    }
    // "toque" silencioso: em alguns navegadores só o resume não marca o documento
    // como audio-allowed; um buffer mudo iniciado SOB o gesto garante a liberação.
    try {
      const buf = unlockCtx.createBuffer(1, 1, 22050);
      const src = unlockCtx.createBufferSource();
      src.buffer = buf;
      src.connect(unlockCtx.destination);
      src.start(0);
    } catch (_) { /* ignore */ }
    // eslint-disable-next-line no-console
    console.log(`${LOG} ensureAudioUnlocked chamado (state=${unlockCtx.state})`);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(`${LOG} ensureAudioUnlocked: erro (segue)`, err?.message || err);
  }
};

/**
 * Mensagem de erro LEGÍVEL p/ toast — nunca deixa uma falha de voz muda ou genérica.
 * Prioriza a mensagem do backend (AppError → { error }) e cai no message do Error.
 */
export const voiceErrorMessage = (err) =>
  err?.response?.data?.error ||
  err?.response?.data?.message ||
  err?.message ||
  (typeof err === "string" ? err : "Erro desconhecido na voz");

/**
 * Normalização DETERMINÍSTICA de número BR p/ o discador (só o discador manual —
 * número de contato/LID do ticket NÃO passa por aqui, o backend resolve LID/9º dígito).
 *  - só dígitos;
 *  - 10-11 dígitos (DDD + número local) → prefixa 55;
 *  - 12-13 dígitos começando com 55 → ok como está;
 *  - senão → bloqueia (curto demais / formato desconhecido).
 * Retorna { ok, number?, message? }. O modal mostra o número final antes de discar.
 */
export const normalizeBrazilianPhone = (raw) => {
  const digits = String(raw || "").replace(/\D/g, "");
  if (!digits) {
    return { ok: false, message: "Digite DDD+número (ex.: 18 99999-9999)." };
  }
  if (digits.length === 10 || digits.length === 11) {
    return { ok: true, number: `55${digits}` };
  }
  if ((digits.length === 12 || digits.length === 13) && digits.startsWith("55")) {
    return { ok: true, number: digits };
  }
  if (digits.length < 10) {
    return { ok: false, message: "Número incompleto — digite DDD+número." };
  }
  return {
    ok: false,
    message: "Número inválido — use DDD+número (Brasil) ou 55+DDD+número."
  };
};

const float32ToInt16LE = (pcm) => {
  const view = new DataView(new ArrayBuffer(pcm.length * 2));
  for (let i = 0; i < pcm.length; i += 1) {
    let s = pcm[i];
    if (Number.isNaN(s)) s = 0;
    else if (s > 1) s = 1;
    else if (s < -1) s = -1;
    view.setInt16(i * 2, s < 0 ? Math.round(s * 32768) : Math.round(s * 32767), true);
  }
  return view.buffer;
};

const int16LEToFloat32 = (buf) => {
  const view = new DataView(buf);
  const n = Math.floor(buf.byteLength / 2);
  const out = new Float32Array(n);
  for (let i = 0; i < n; i += 1) out[i] = view.getInt16(i * 2, true) / 32768;
  return out;
};

/**
 * Abre a chamada WebRTC contra o motor (via proxy do backend).
 * @param {number} whatsappId
 * @param {string} callId
 * @param {string|null} micDeviceId
 * @returns {Promise<{ remoteStream: MediaStream, close: () => void }>}
 */
export const openGalleraCall = async (whatsappId, callId, micDeviceId = null, record = false, clientId = null, onFirstRemoteAudio = null) => {
  // eslint-disable-next-line no-console
  console.log(`${LOG} abrindo áudio da chamada`, { whatsappId, callId, micDeviceId, record, hasClientId: !!clientId });
  let micStream;
  try {
    micStream = await navigator.mediaDevices.getUserMedia({
      audio: micDeviceId ? { deviceId: { exact: micDeviceId } } : true
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(`${LOG} getUserMedia FALHOU:`, err?.name, err?.message || err);
    if (err?.name === "NotAllowedError" || err?.name === "SecurityError") {
      throw new Error("Permissão do microfone negada — libere o microfone para este site (cadeado na barra de endereço) e tente de novo.");
    }
    if (err?.name === "NotFoundError" || err?.name === "OverconstrainedError") {
      throw new Error("Microfone não encontrado — conecte/selecione um microfone e tente de novo.");
    }
    throw new Error(`Microfone indisponível: ${err?.message || err?.name || "erro desconhecido"}`);
  }

  // Modelo por token (.254): o motor central pode exigir TURN/STUN (coturn). Buscamos a
  // config da conexão (signalingUrl fica no backend; aqui só precisamos dos iceServers).
  // À prova de falha: se /config falhar, seguimos com [] (motor local/NAT1:1 legado).
  let iceServers = [];
  try {
    const { data: cfg } = await api.get(`/voice/${whatsappId}/config`);
    if (cfg && Array.isArray(cfg.iceServers)) iceServers = cfg.iceServers;
    // eslint-disable-next-line no-console
    console.log(`${LOG} config da conexão ${whatsappId}:`, cfg?.enabled, cfg?.via || "", `${iceServers.length} iceServer(s)`);
  } catch (err) {
    /* config indisponível — segue sem TURN (comportamento legado), mas LOGA */
    // eslint-disable-next-line no-console
    console.warn(`${LOG} GET /voice/${whatsappId}/config falhou (segue sem TURN):`, err?.response?.status, voiceErrorMessage(err));
  }

  const pc = new RTCPeerConnection({ iceServers });

  const dc = pc.createDataChannel(PCM_CHANNEL_LABEL, { ordered: true });
  dc.binaryType = "arraybuffer";
  dc.onopen = () => {
    // marco de depuração: canal "pcm" de pé = mídia negociada dos DOIS lados
    // (vale p/ LIGAR e ATENDER — os dois passam por esta MESMA função).
    // eslint-disable-next-line no-console
    console.log(`${LOG} DataChannel "pcm" ABERTO — mídia da chamada ${callId} de pé`);
  };

  // Destrava preventiva: se algum clique já passou por ensureAudioUnlocked, o
  // documento está audio-allowed e o ctx abaixo já nasce liberável mesmo aqui
  // (outbound roda num useEffect, fora do gesto). Idempotente/barato.
  ensureAudioUnlocked();
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  const ctx = new AudioCtx({ sampleRate: SAMPLE_RATE });
  // EVIDÊNCIA (peça ao dono p/ olhar no console ao LIGAR): "suspended" logo na
  // criação = navegador bloqueou por autoplay; precisa do gesto/ensureAudioUnlocked.
  // eslint-disable-next-line no-console
  console.log(`${LOG} audioContext.state (criação, ${callId}) = ${ctx.state}`);
  try {
    await ctx.audioWorklet.addModule(CAPTURE_WORKLET_URL);
    await ctx.audioWorklet.addModule(PLAYBACK_WORKLET_URL);
    // Sem "running" o AudioWorklet de playback NÃO processa o PCM do celular →
    // streamDest fica mudo mesmo com o <audio> tocando. Resume + 1 re-tentativa.
    await ctx.resume();
    if (ctx.state !== "running") {
      // eslint-disable-next-line no-console
      console.warn(`${LOG} audioContext.state ainda "${ctx.state}" após 1º resume (${callId}) — re-tentando`);
      await ctx.resume().catch(() => {});
    }
    // eslint-disable-next-line no-console
    console.log(
      `${LOG} audioContext.state (após resume, ${callId}) = ${ctx.state} ` +
        (ctx.state === "running"
          ? "✓ ÁUDIO LIBERADO"
          : "✗ AINDA BLOQUEADO — SEM SOM (autoplay: faltou gesto/ensureAudioUnlocked)")
    );
  } catch (err) {
    // worklet não carregou (404/cache velho do service worker) — sem ele não há áudio
    // eslint-disable-next-line no-console
    console.error(`${LOG} falha ao carregar AudioWorklet (${CAPTURE_WORKLET_URL}):`, err?.message || err);
    try { micStream.getTracks().forEach((t) => t.stop()); } catch (_) {}
    try { ctx.close(); } catch (_) {}
    try { pc.close(); } catch (_) {}
    throw new Error("Falha ao carregar o módulo de áudio da chamada. Recarregue a página (Ctrl+Shift+R) e tente de novo.");
  }

  const micSource = ctx.createMediaStreamSource(micStream);
  const captureNode = new AudioWorkletNode(ctx, CAPTURE_PROCESSOR_NAME);
  captureNode.port.onmessage = (e) => {
    if (dc.readyState === "open") dc.send(float32ToInt16LE(e.data));
  };
  micSource.connect(captureNode);
  captureNode.connect(ctx.destination);

  const playbackNode = new AudioWorkletNode(ctx, PLAYBACK_PROCESSOR_NAME);
  const streamDest = ctx.createMediaStreamDestination();
  playbackNode.connect(streamDest);
  // 1º pacote de áudio REMOTO = melhor sinal client-side de que a chamada conectou.
  // Usado como FALLBACK no outbound quando o evento "answered" do socket não chega
  // (caminho por token) — sem isso o card ficava preso em "Chamando…" com ringback
  // tocando por cima da conversa.
  let gotRemoteAudio = false;
  dc.onmessage = (e) => {
    if (!gotRemoteAudio) {
      gotRemoteAudio = true;
      // eslint-disable-next-line no-console
      console.log(`${LOG} primeiro áudio remoto da chamada ${callId} chegou`);
      if (typeof onFirstRemoteAudio === "function") {
        try { onFirstRemoteAudio(); } catch (_) { /* ignore */ }
      }
    }
    playbackNode.port.postMessage(int16LEToFloat32(e.data));
  };

  // GRAVAÇÃO (opcional): mistura mic (atendente) + playback (contato) num só stream
  // e grava via MediaRecorder. O blob é enviado ao backend no fim → S3 (não toca o
  // disco da VM). Só liga se `record` = true (servidor com gravação habilitada).
  // GRAVAÇÃO em um AudioContext SEPARADO (qualidade normal ~48kHz, fora do canal 16kHz
  // da ligação e sem os AudioWorklets no meio). Isso evita robotização/loop e garante
  // que os DOIS lados entram: mic (getUserMedia) + contato (remoteStream que o atendente
  // ouve). É tudo no navegador do atendente — não pesa no servidor.
  let recorder = null;
  let recCtx = null;
  const recChunks = [];
  if (record) {
    try {
      const RecCtx = window.AudioContext || window.webkitAudioContext;
      recCtx = new RecCtx(); // sample rate padrão do device (não força 16k)
      const recDest = recCtx.createMediaStreamDestination();

      // MIC (atendente) — stream cru do getUserMedia
      const micSrc = recCtx.createMediaStreamSource(micStream);
      const micG = recCtx.createGain();
      micG.gain.value = 1.0;
      micSrc.connect(micG);
      micG.connect(recDest);

      // CONTATO (voz do celular) — o MESMO stream que toca no fone (streamDest.stream)
      const remoteSrc = recCtx.createMediaStreamSource(streamDest.stream);
      const remG = recCtx.createGain();
      remG.gain.value = 1.0;
      remoteSrc.connect(remG);
      remG.connect(recDest);

      const mime = window.MediaRecorder && MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : "";
      recorder = mime ? new MediaRecorder(recDest.stream, { mimeType: mime }) : new MediaRecorder(recDest.stream);
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size) recChunks.push(e.data);
      };
      recorder.start(1000);
    } catch (err) {
      recorder = null; // navegador sem suporte → segue sem gravar (não quebra a ligação)
    }
  }

  const stopRecording = () =>
    new Promise((resolve) => {
      if (!recorder || recorder.state === "inactive") {
        resolve(recChunks.length ? new Blob(recChunks, { type: "audio/webm" }) : null);
        return;
      }
      recorder.onstop = () =>
        resolve(recChunks.length ? new Blob(recChunks, { type: "audio/webm" }) : null);
      try {
        recorder.stop();
      } catch (_) {
        resolve(recChunks.length ? new Blob(recChunks, { type: "audio/webm" }) : null);
      }
    });

  const offer = await pc.createOffer();
  await pc.setLocalDescription(offer);
  await new Promise((resolve) => {
    if (pc.iceGatheringState === "complete") return resolve();
    // timeout de 1s: não trava a chamada se o ICE não completar (candidatos locais bastam)
    const t = setTimeout(resolve, 1000);
    pc.addEventListener("icegatheringstatechange", () => {
      if (pc.iceGatheringState === "complete") {
        clearTimeout(t);
        resolve();
      }
    });
  });

  // eslint-disable-next-line no-console
  console.log(`${LOG} POST /voice/${whatsappId}/calls/${callId}/webrtc (troca de SDP)…`);
  let data;
  try {
    ({ data } = await api.post(`/voice/${whatsappId}/calls/${callId}/webrtc`, {
      sdp_offer: pc.localDescription.sdp,
      // OUTBOUND: manda o owner da chamada (gv-<user>-out-<nonce> devolvido pelo
      // startCall); sem ele o motor não acha a call (404) e a saída morre sem áudio.
      // INBOUND: clientId null → o backend usa o determinístico gv-<user>-<callId>.
      // Regressão conhecida: [[project_voz_outbound_clientid_regressao]].
      ...(clientId ? { clientId } : {})
    }));
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(`${LOG} /webrtc FALHOU:`, err?.response?.status, voiceErrorMessage(err));
    try { if (recCtx) recCtx.close(); } catch (_) {}
    try { micStream.getTracks().forEach((t) => t.stop()); } catch (_) {}
    try { ctx.close(); } catch (_) {}
    try { pc.close(); } catch (_) {}
    throw err;
  }
  const sdpAnswer = data && data.sdp_answer;
  if (!sdpAnswer) {
    // eslint-disable-next-line no-console
    console.error(`${LOG} /webrtc respondeu sem sdp_answer:`, data);
    try { if (recCtx) recCtx.close(); } catch (_) {}
    try { micStream.getTracks().forEach((t) => t.stop()); } catch (_) {}
    try { ctx.close(); } catch (_) {}
    try { pc.close(); } catch (_) {}
    throw new Error("Motor de voz não devolveu a resposta WebRTC");
  }
  await pc.setRemoteDescription({ type: "answer", sdp: sdpAnswer });
  // eslint-disable-next-line no-console
  console.log(`${LOG} WebRTC negociado — áudio da chamada ${callId} aberto`);

  return {
    remoteStream: streamDest.stream,
    stopRecording,
    // Mudo: desliga o áudio do microfone (o contato para de ouvir o atendente).
    setMuted: (m) => {
      try {
        micStream.getAudioTracks().forEach((t) => {
          t.enabled = !m;
        });
      } catch (_) {
        /* ignore */
      }
    },
    close: () => {
      try { if (recorder && recorder.state !== "inactive") recorder.stop(); } catch (_) {}
      try { if (recCtx) recCtx.close(); } catch (_) {}
      try { micStream.getTracks().forEach((t) => t.stop()); } catch (_) {}
      try { ctx.close(); } catch (_) {}
      try { pc.close(); } catch (_) {}
    }
  };
};

/**
 * INBOUND — ATENDER uma ligação recebida. SIMETRIA GARANTIDA COM O LIGAR:
 * o caminho de mídia é EXATAMENTE o mesmo do outbound — este helper só faz o
 * accept e delega a mídia ao MESMO `openGalleraCall` do LIGAR (getUserMedia →
 * RTCPeerConnection → DataChannel "pcm" → POST /voice/:id/calls/:callId/webrtc →
 * AudioWorklets). NUNCA atender sem esta negociação: sem o POST /webrtc o motor
 * não cria o Bridge da chamada (cmd/server/session.go: OnPeerAudio descarta o
 * PCM do celular com `bridge == nil`) — foi a causa raiz do "atendeu e ficou
 * mudo" (regressão 2026-07-06).
 *
 * Fluxo: POST /voice/:id/answer (motor aceita no WhatsApp e devolve o clientId
 * owner `gv-<user>-<callId>`) → openGalleraCall com ESSE clientId — mesmo
 * contrato do fix outbound-clientid ([[project_voz_outbound_clientid_regressao]]);
 * no caminho por token o backend/painel ignora o clientId do body (deriva do
 * token), então mandar é inócuo lá e necessário no legado.
 *
 * @param {number} whatsappId
 * @param {string} callId
 * @param {object} [opts] { micDeviceId, record, onFirstRemoteAudio }
 * @returns {Promise<{ rtc: { remoteStream, setMuted, stopRecording, close }, clientId: string|null }>}
 */
export const answerGalleraInboundCall = async (
  whatsappId,
  callId,
  { micDeviceId = null, record = false, onFirstRemoteAudio = null } = {}
) => {
  // eslint-disable-next-line no-console
  console.log(`${LOG} ATENDER: aceitando chamada recebida`, { whatsappId, callId });
  let data;
  try {
    ({ data } = await api.post(`/voice/${whatsappId}/answer`, { callId }));
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(`${LOG} ATENDER: POST /voice/${whatsappId}/answer FALHOU:`, err?.response?.status, voiceErrorMessage(err));
    throw err;
  }
  const clientId = (data && data.clientId) || null;
  // eslint-disable-next-line no-console
  console.log(`${LOG} ATENDER: chamada ${callId} aceita no motor — abrindo a mídia pelo MESMO caminho do LIGAR (/webrtc)`, { clientId });
  const rtc = await openGalleraCall(
    whatsappId,
    callId,
    micDeviceId,
    record,
    clientId,
    onFirstRemoteAudio
  );
  return { rtc, clientId };
};

/**
 * Tom de chamada (via WebAudio, sem arquivo). `kind`:
 *  - "incoming" (cliente ligando p/ você): insistente/forte, gap curto — pra CHAMAR
 *    atenção do atendente (dois tons alternados, tipo campainha).
 *  - "ringback" (você ligando p/ o cliente): "trim-trim" suave, gap longo — só confirma
 *    que está discando (igual o tom que se ouve ao ligar).
 * Retorna função p/ PARAR. Best-effort: se o navegador bloquear áudio, não toca.
 */
export const startRingtone = (kind = "incoming") => {
  let ctx = null;
  let stopped = false;
  let timer = null;
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    ctx = new AudioCtx();
    if (ctx.state === "suspended") ctx.resume().catch(() => {});

    const beep = (start, dur, freq, vol) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(vol, start + 0.02);
      gain.gain.setValueAtTime(vol, start + dur - 0.03);
      gain.gain.linearRampToValueAtTime(0, start + dur);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(start);
      osc.stop(start + dur);
    };

    const ringback = () => {
      if (stopped || !ctx) return;
      const t = ctx.currentTime;
      beep(t + 0.0, 0.4, 425, 0.16);
      beep(t + 0.5, 0.4, 425, 0.16);
      timer = setTimeout(ringback, 4000); // pausa longa
    };
    const incoming = () => {
      if (stopped || !ctx) return;
      const t = ctx.currentTime;
      // Toque clássico de telefone: dois tons juntos (440+480 Hz) formam o "briiing"
      // quente e reconhecível. Padrão "briiing … briiing … pausa" (tipo campainha).
      const ring = (start, dur) => {
        beep(start, dur, 440, 0.24);
        beep(start, dur, 480, 0.24);
      };
      ring(t + 0.0, 1.0);
      ring(t + 1.4, 1.0);
      timer = setTimeout(incoming, 3000);
    };

    (kind === "ringback" ? ringback : incoming)();
  } catch (_) {
    /* navegador bloqueou áudio — segue sem tom */
  }

  return () => {
    stopped = true;
    if (timer) clearTimeout(timer);
    try {
      if (ctx) ctx.close();
    } catch (_) {
      /* ignore */
    }
  };
};

/** Lista microfones disponíveis (para o seletor de dispositivo). */
export const listMicrophones = async () => {
  try {
    // precisa de permissão p/ os labels aparecerem
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices
      .filter((d) => d.kind === "audioinput")
      .map((d) => ({ id: d.deviceId, label: d.label || "Microfone" }));
  } catch (_) {
    return [];
  }
};

/**
 * Sobe a gravação (Blob) da chamada pro backend, que guarda no S3/local e vincula
 * ao CallLog pelo callId. Best-effort — falha aqui não afeta a ligação.
 */
export const uploadGalleraRecording = async (whatsappId, callId, blob) => {
  if (!blob || !blob.size) return;
  const form = new FormData();
  form.append("callId", String(callId));
  form.append("recording", blob, `call_${callId}.webm`);
  // NÃO setar Content-Type manualmente: o navegador precisa gerar o boundary do
  // multipart sozinho, senão o multer no servidor não parseia (upload pendura/falha).
  // eslint-disable-next-line no-console
  console.log("[gravacao] enviando", blob.size, "bytes p/ /voice/" + whatsappId + "/recording");
  const resp = await api.post(`/voice/${whatsappId}/recording`, form, { timeout: 60000 });
  // eslint-disable-next-line no-console
  console.log("[gravacao] resposta do servidor:", resp && resp.status, resp && resp.data);
  return resp && resp.data;
};

/**
 * OUTBOUND — origina uma chamada de saída (click-to-call no ticket).
 * Pede ao backend para o motor discar (`POST /voice/:whatsappId/call`) e, com o
 * callId de volta, dispara o window event que o overlay GalleraVoiceCall escuta
 * (abre o card "chamando…" e o WebRTC). Assim o botão do ticket não precisa
 * conhecer o overlay global.
 *
 * @param {number} whatsappId  conexão de voz do ticket
 * @param {string} number      número do contato (com/sem máscara)
 * @param {object} [info]      { contactName, whatsappName } p/ exibir no card
 * @returns {Promise<string>}  callId originado
 */
export const startGalleraOutboundCall = async (whatsappId, number, info = {}) => {
  const digits = String(number || "").replace(/\D/g, "");
  // eslint-disable-next-line no-console
  console.log(`${LOG} originando chamada de saída`, { whatsappId, toNumber: digits });
  if (!whatsappId || Number.isNaN(Number(whatsappId))) {
    // eslint-disable-next-line no-console
    console.error(`${LOG} originar ABORTADO: whatsappId inválido:`, whatsappId);
    throw new Error("Conexão de voz inválida — selecione a conexão e tente de novo.");
  }
  if (!digits) {
    // eslint-disable-next-line no-console
    console.error(`${LOG} originar ABORTADO: número vazio (entrada: "${number}")`);
    throw new Error("Número de destino vazio — digite DDD+número.");
  }
  let data;
  try {
    ({ data } = await api.post(`/voice/${whatsappId}/call`, { toNumber: digits }));
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(`${LOG} POST /voice/${whatsappId}/call FALHOU:`, err?.response?.status, voiceErrorMessage(err));
    throw err;
  }
  // eslint-disable-next-line no-console
  console.log(`${LOG} chamada originada:`, data && data.callId, data && data.clientId ? "(com clientId)" : "(sem clientId)");
  const callId = data && data.callId;
  // clientId (owner gv-<user>-out-<nonce>) — OBRIGATÓRIO repassar ao /webrtc e /hangup,
  // senão o motor não acha a chamada de saída (404) e a ligação morre sem áudio.
  // Regressão conhecida: [[project_voz_outbound_clientid_regressao]].
  const clientId = data && data.clientId;
  if (!callId) {
    // eslint-disable-next-line no-console
    console.error(`${LOG} resposta do /call sem callId:`, data);
    throw new Error("Motor de voz não devolveu o identificador da chamada");
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent("galleraVoice:startCall", {
        detail: {
          whatsappId,
          callId,
          clientId,
          number: digits,
          contactName: info.contactName || "",
          whatsappName: info.whatsappName || ""
        }
      })
    );
  }
  return callId;
};
